import React, { useEffect, useState, useRef, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { truncateString } from "../helpers/truncateString.js";
import Lottie from "lottie-react";
import hexagonUpAnimation from "../animations/hex-arrange-in-v1-up.json";
import hexagonDownAnimation from "../animations/hex-arrange-in-v2-down.json";
import geometryLogoAnimation from "../animations/geometry-logo-animation.json";
import textAnimation from "../animations/geometry-text-in.json";
import { SubstackFeedContext } from "../components/SubstackFeedProvider";
import { useLocation } from "react-router-dom";

export default function WritingPage({
  callback,
  callback2,
  metaTitle,
  metaDescription,
  metaKeywords,
}) {
  // Feed comes pre-loaded from context for a near-instant experience
  const feedPosts = useContext(SubstackFeedContext);
  // posts === undefined -> loading, null -> error, []/array -> data
  const [posts, setPosts] = useState(feedPosts);
  const location = useLocation();
  const disableAnim = new URLSearchParams(location.search).get("static") === "1";

  // Animation refs
  const hexagonDown = useRef();
  const hexagonUp = useRef();
  const logo = useRef();
  const text = useRef();

  useEffect(() => {
    // Callbacks coming from Layout to adjust theme
    callback(false);
    callback2(false);

    // For desktop keep left-side static (no flicker). Mobile can still animate slightly.
    const isDesktop = window.innerWidth >= 1024;

    if (isDesktop) {
      hexagonDown.current?.goToAndStop(50, true);
      hexagonUp.current?.goToAndStop(50, true);

      if (disableAnim) {
        logo.current?.goToAndStop(100, true);
        text.current?.goToAndStop(1, true);
      } else {
        logo.current?.playSegments([0, 100], true);
        text.current?.setDirection(-1);
        text.current?.playSegments([50, 1], true);
      }
    } else {
      // Mobile retains original approach (no left column so animations hidden)
      hexagonDown.current?.playSegments([0, 50], true);
      hexagonUp.current?.playSegments([0, 50], true);
    }
  }, [callback, callback2, disableAnim]);

  useEffect(() => {
    // If the provider finished (success or error) update local state
    if (feedPosts !== undefined) {
      setPosts(feedPosts);
    }
  }, [feedPosts]);

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title data-rh="true">{metaTitle || "Writing | Geometry"}</title>
        <meta
          name="description"
          content={truncateString(
            metaDescription || "Geometry's writing and insights",
            152,
          )}
          data-rh="true"
        />
        <meta name="keywords" content={metaKeywords} data-rh="true" />
      </Helmet>
      <div className="relative h-full w-full overflow-hidden">
        {/* Background animations */}
        <div className="fixed left-0 top-0 z-0 h-screen w-screen">
          <Lottie
            lottieRef={hexagonDown}
            className="absolute scale-50 lg:scale-100"
            style={{ left: "-300px", width: "600px", top: "-200px" }}
            animationData={hexagonDownAnimation}
            autoplay={false}
            loop={false}
          />
          <Lottie
            lottieRef={hexagonUp}
            className="absolute scale-50 lg:scale-100"
            style={{ right: "-300px", width: "600px", bottom: "-300px" }}
            animationData={hexagonUpAnimation}
            autoplay={false}
            loop={false}
          />
        </div>

        {/* Main two-column layout */}
        <div className="no-scroll absolute z-10 block h-full w-full flex-row items-center overflow-y-auto lg:-top-20 lg:flex">
          {/* Left static section (hidden on mobile) */}
          <div className="hidden lg:flex mb-3 flex w-full flex-row flex-nowrap items-center pr-0 lg:mb-0 lg:w-auto lg:pr-20">
            <Lottie
              lottieRef={logo}
              className="logo mr-4 h-20 w-20 flex-none lg:h-32 lg:w-32 2xl:h-40 2xl:w-40 text-primary"
              animationData={geometryLogoAnimation}
              autoplay={false}
              loop={false}
            />
            <Lottie
              lottieRef={text}
              className="logo h-full w-48 lg:w-60 2xl:w-72 text-primary"
              animationData={textAnimation}
              autoplay={false}
              loop={false}
            />
          </div>

          {/* Divider line for desktop */}
          <div className="hidden lg:block w-0.5 h-full bg-cyan mx-10 xl:mx-16 z-20"></div>

          {/* Right side – list of posts */}
          <div className="border-cyan fade-in z-20 w-full flex-1 grow border-0 pb-6 pl-0 lg:w-auto lg:border-l lg:pb-0 lg:pl-16 lg:pr-10 overflow-y-auto">
            <h1 className="font-clarikageo text-2xl font-medium text-primary mb-6 lg:hidden">Writing</h1>
            <div className="space-y-4 max-w-4xl">
              {posts === undefined ? (
                <p className="font-clarikageo text-lg text-gray/70">Loading posts…</p>
              ) : posts === null ? (
                <p className="font-clarikageo text-lg text-gray/70">
                  Unable to load posts right now. Please try again later.
                </p>
              ) : posts.length ? (
                posts.map((post, index) => (
                  <a
                    key={index}
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-6 bg-transparent border-2 border-gray/30 rounded-lg hover:bg-gray/5 hover:border-gray/50 transition-all"
                  >
                    <h2 className="font-clarikageo text-xl font-medium text-primary mb-2">
                      {post.title}
                    </h2>
                    <p className="font-clarikageo text-lg font-light text-gray/80" dangerouslySetInnerHTML={{__html: post.description}} />
                    <p className="font-clarikageo text-sm font-light text-gray/60 mt-2">
                      {post.pubDate.toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </a>
                ))
              ) : (
                <p className="font-clarikageo text-lg text-gray/70">No posts found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}